import { useState, useEffect, useCallback, useRef } from 'react';

interface PaginatedResponse<T> {
  items: T[];
  continuationToken?: string;
}

interface UsePaginatedApiOptions {
  enabled?: boolean;
}

interface UsePaginatedApiResult<T> {
  data: T[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePaginatedApi<TParams, TItem>(
  fetchFn: (params: TParams & { continuationToken?: string }) => Promise<PaginatedResponse<TItem>>,
  params: TParams,
  options?: UsePaginatedApiOptions,
): UsePaginatedApiResult<TItem> {
  const [data, setData] = useState<TItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const enabled = options?.enabled ?? true;
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setFetchingMore(false);
      return;
    }

    cancelledRef.current = false;
    setLoading(true);
    setFetchingMore(false);
    setError(null);
    setData([]);

    async function fetchAllPages() {
      const accumulated: TItem[] = [];
      let token: string | undefined;
      let isFirstPage = true;

      try {
        do {
          const fetchParams = { ...params, ...(token ? { continuationToken: token } : {}) } as TParams & { continuationToken?: string };
          const response = await fetchFn(fetchParams);
          if (cancelledRef.current) return;
          accumulated.push(...response.items);
          setData([...accumulated]);
          token = response.continuationToken;

          if (isFirstPage && token) {
            setLoading(false);
            setFetchingMore(true);
            isFirstPage = false;
          }
        } while (token);
      } catch (err) {
        if (cancelledRef.current) return;
        setData([...accumulated]);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
          setFetchingMore(false);
        }
      }
    }

    fetchAllPages();

    return () => {
      cancelledRef.current = true;
    };
  }, [JSON.stringify(params), refetchTrigger, enabled]);

  const refetch = useCallback(() => setRefetchTrigger((prev) => prev + 1), []);

  return { data, loading, fetchingMore, error, refetch };
}
